import axios from 'axios';

const API_BASE_URL = 'https://api.eventoshub.suils.es/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Header estándar (para desarrollo local)
      config.headers.Authorization = `Bearer ${token}`;
      // Header personalizado (para producción que bloquea Authorization)
      config.headers['X-Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interfaces
interface LoginData {
  username: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  name: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  name: string;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface Room {
  id: number;
  name: string;
  description: string;
  code: string;
  member_count: number;
  is_admin: boolean;
  created_at: string;
}

interface CreateRoomData {
  name: string;
  description?: string;
}

interface JoinRoomData {
  code: string;
}

interface Nominee {
  id: number;
  room_id: number;
  user_id: number;
  name: string;
  description?: string;
  creator_username: string;
  creator_name: string;
  photo_count: number;
  nickname_count: number;
  created_at: string;
}

interface CreateNomineeData {
  room_id: number;
  user_id: number;
  name: string;
  description?: string;
}

interface VoteData {
  room_id: number;
  category_id: number;
  nominee_id: number;
}

interface NicknameData {
  nickname: string;
  nominee_id: number;
  room_id: number;
}

// API Auth
export const authAPI = {
  login: async (data: LoginData): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data.data; // Acceder al data anidado
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data);
    return response.data.data; // Acceder al data anidado
  },

  verifyToken: async (): Promise<User> => {
    const response = await api.get('/auth/verify');
    return response.data.data; // Acceder al data anidado
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// API Rooms
export const roomAPI = {
  getMyRooms: async (): Promise<Room[]> => {
    const response = await api.get('/rooms/my-rooms');
    return response.data.data;
  },

  createRoom: async (data: CreateRoomData): Promise<{ room_id: number }> => {
    const response = await api.post('/rooms/create', data);
    return response.data.data;
  },

  joinRoom: async (data: JoinRoomData): Promise<{ room_id: number }> => {
    const response = await api.post('/rooms/join', data);
    return response.data.data;
  },

  getRoomDetails: async (roomId: number) => {
    const response = await api.get(`/rooms/details?room_id=${roomId}`);
    return response.data.data;
  },

  getRoomMembers: async (roomId: number) => {
    const response = await api.get(`/rooms/members?room_id=${roomId}`);
    return response.data.data;
  },
};

// API Nominees
export const nomineeAPI = {
  getRoomNominees: async (roomId: number): Promise<Nominee[]> => {
    const response = await api.get(`/nominees/list?room_id=${roomId}`);
    return response.data.data;
  },

  getVotingNominees: async (roomId: number): Promise<Nominee[]> => {
    const response = await api.get(`/nominees/voting-list?room_id=${roomId}`);
    return response.data.data;
  },

  createNominee: async (data: CreateNomineeData): Promise<{ nominee_id: number }> => {
    const response = await api.post('/nominees/create', data);
    return response.data.data;
  },

  getNomineeDetails: async (nomineeId: number) => {
    const response = await api.get(`/nominees/details?nominee_id=${nomineeId}`);
    return response.data.data;
  },

  updateNominee: async (nomineeId: number, data: Omit<CreateNomineeData, 'room_id' | 'user_id'>) => {
    const response = await api.put(`/nominees/update?nominee_id=${nomineeId}`, data);
    return response.data;
  },

  deleteNominee: async (nomineeId: number) => {
    const response = await api.delete(`/nominees/delete?nominee_id=${nomineeId}`);
    return response.data;
  },
};

// API Voting
export const votingAPI = {
  vote: async (data: VoteData) => {
    const response = await api.post('/votes', data);
    return response.data.data;
  },

  getResults: async (roomId?: number) => {
    const url = roomId ? `/votes/results?room_id=${roomId}` : '/votes/results';
    const response = await api.get(url);
    return response.data.data;
  },

  getCategories: async (roomId?: number) => {
    const url = roomId ? `/categories?room_id=${roomId}` : '/categories';
    const response = await api.get(url);
    return response.data.data;
  },

  getNominees: async (roomId?: number) => {
    const url = roomId ? `/nominees/list?room_id=${roomId}` : '/nominees';
    const response = await api.get(url);
    return response.data.data;
  },
};

// API Photos  
export const photoAPI = {
  uploadPhoto: async (roomId: number, nomineeId: number, photo: File) => {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('room_id', roomId.toString());
    formData.append('nominee_id', nomineeId.toString());
    
    const response = await api.post('/photos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  getNomineePhotos: async (nomineeId: number) => {
    const response = await api.get(`/photos/nominee?nominee_id=${nomineeId}`);
    return response.data.data;
  },

  getPhotosByNominee: async (nomineeId: number) => {
    const response = await api.get(`/photos/by-nominee?nominee_id=${nomineeId}`);
    return response.data.data;
  },

  getRoomPhotos: async (roomId: number) => {
    const response = await api.get(`/photos/room?room_id=${roomId}`);
    return response.data.data;
  },

  deletePhoto: async (photoId: number) => {
    const response = await api.delete(`/photos/delete?photo_id=${photoId}`);
    return response.data;
  },
};

// API Nicknames
export const nicknameAPI = {
  submit: async (data: NicknameData) => {
    const response = await api.post('/nicknames', data);
    return response.data.data;
  },

  getNicknames: async (roomId?: number) => {
    const url = roomId ? `/nicknames?room_id=${roomId}` : '/nicknames';
    const response = await api.get(url);
    return response.data.data;
  },

  voteNickname: async (nicknameId: number) => {
    const response = await api.post(`/nicknames/vote`, { nickname_id: nicknameId });
    return response.data;
  },

  getNicknamesByNominee: async (nomineeId: number) => {
    const response = await api.get(`/nicknames?action=by-nominee&nominee_id=${nomineeId}`);
    return response.data.data;
  },
};

export default api;